(() => {
  const focusableSelector = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  class SOHeaderPremium {
    constructor(root) {
      if (root.dataset.soPremiumReady === 'true') return;
      root.dataset.soPremiumReady = 'true';
      this.root = root;
      this.sectionHost = root.closest('.so-premium-section');
      this.drawer = root.querySelector('[data-so-premium-drawer]');
      this.drawerOpen = root.querySelector('[data-so-premium-drawer-open]');
      this.drawerClose = root.querySelector('[data-so-premium-drawer-close]');
      this.overlay = root.querySelector('[data-so-premium-overlay]');
      this.search = root.querySelector('[data-so-premium-search]');
      this.searchOpen = root.querySelector('[data-so-premium-search-open]');
      this.searchClose = root.querySelector('[data-so-premium-search-close]');
      this.searchInput = root.querySelector('[data-so-premium-search-input]');
      this.lastFocus = null;
      this.menuCloseTimers = new WeakMap();
      this.pinnedMenus = new WeakSet();
      this.abortController = new AbortController();
      this.signal = this.abortController.signal;
      this.sectionHost?.classList.toggle('so-premium-section--sticky', root.dataset.sticky === 'true');
      this.bindEvents();
      this.updateScrollState();
    }

    bindEvents() {
      const eventOptions = { signal: this.signal };
      const passiveOptions = { passive: true, signal: this.signal };

      window.addEventListener('scroll', () => this.updateScrollState(), passiveOptions);
      this.drawerOpen?.addEventListener('click', () => this.openDrawer(), eventOptions);
      this.drawerClose?.addEventListener('click', () => this.closeDrawer(), eventOptions);
      this.overlay?.addEventListener('click', () => this.closeDrawer(), eventOptions);
      this.searchOpen?.addEventListener('click', () => this.toggleSearch(), eventOptions);
      this.searchClose?.addEventListener('click', () => this.closeSearch(true), eventOptions);

      this.root.querySelector('[data-so-premium-dismiss]')?.addEventListener('click', (event) => {
        event.currentTarget.closest('[data-so-premium-announcement]')?.remove();
      }, eventOptions);

      this.root.querySelectorAll('[data-so-premium-language]').forEach((select) => {
        select.addEventListener('change', (event) => event.currentTarget.form?.submit(), eventOptions);
      });

      this.root.querySelectorAll('[data-so-premium-menu]').forEach((menu) => {
        const trigger = menu.querySelector('[data-so-premium-menu-trigger]');
        if (!trigger) return;
        trigger.addEventListener('click', (event) => {
          event.stopPropagation();
          this.toggleMenu(menu, true);
        }, eventOptions);
        menu.addEventListener('mouseenter', () => {
          this.cancelMenuClose(menu);
          if (window.matchMedia('(hover: hover) and (min-width: 1021px)').matches) this.openMenu(menu);
        }, eventOptions);
        menu.addEventListener('mouseleave', () => {
          if (
            window.matchMedia('(hover: hover) and (min-width: 1021px)').matches
            && !this.pinnedMenus.has(menu)
          ) this.scheduleMenuClose(menu);
        }, eventOptions);
      });

      this.root.querySelectorAll('[data-so-premium-accordion]').forEach((accordion) => {
        accordion.querySelector('[data-so-premium-accordion-trigger]')?.addEventListener('click', () => {
          const willOpen = !accordion.classList.contains('is-open');
          accordion.classList.toggle('is-open', willOpen);
          accordion.querySelector('[data-so-premium-accordion-trigger]')
            ?.setAttribute('aria-expanded', String(willOpen));
        }, eventOptions);
      });

      document.addEventListener('click', (event) => {
        const clickedMenu = event.target.closest('[data-so-premium-menu]');
        if (!clickedMenu || !this.root.contains(clickedMenu)) this.closeMenus();
        if (this.search?.classList.contains('is-open') && !event.target.closest('[data-so-premium-search], [data-so-premium-search-open]')) {
          this.closeSearch(false);
        }
      }, eventOptions);

      document.addEventListener('keydown', (event) => this.handleKeydown(event), eventOptions);
      document.addEventListener('so-premium:drawer-open', (event) => {
        if (event.detail !== this.root) this.closeDrawer(false);
      }, eventOptions);
      document.addEventListener('shopify:section:unload', (event) => {
        if (event.target.contains(this.root)) this.destroy();
      }, eventOptions);
    }

    updateScrollState() {
      this.root.classList.toggle('is-scrolled', window.scrollY > 20);
    }

    getMenus() {
      return [...this.root.querySelectorAll('[data-so-premium-menu]')];
    }

    openMenu(menu) {
      this.cancelMenuClose(menu);
      this.closeMenus(menu);
      menu.classList.add('is-open');
      menu.querySelector('[data-so-premium-menu-trigger]')?.setAttribute('aria-expanded', 'true');
      menu.querySelector('[data-so-premium-menu-panel]')?.setAttribute('aria-hidden', 'false');
      this.closeSearch(false);
    }

    closeMenu(menu) {
      this.cancelMenuClose(menu);
      this.pinnedMenus.delete(menu);
      menu.classList.remove('is-open');
      menu.querySelector('[data-so-premium-menu-trigger]')?.setAttribute('aria-expanded', 'false');
      menu.querySelector('[data-so-premium-menu-panel]')?.setAttribute('aria-hidden', 'true');
    }

    toggleMenu(menu, pin = false) {
      if (menu.classList.contains('is-open')) {
        this.closeMenu(menu);
      } else {
        this.openMenu(menu);
        if (pin) this.pinnedMenus.add(menu);
      }
    }

    scheduleMenuClose(menu) {
      this.cancelMenuClose(menu);
      const timer = window.setTimeout(() => this.closeMenu(menu), 220);
      this.menuCloseTimers.set(menu, timer);
    }

    cancelMenuClose(menu) {
      const timer = this.menuCloseTimers.get(menu);
      if (timer) window.clearTimeout(timer);
      this.menuCloseTimers.delete(menu);
    }

    closeMenus(except = null) {
      this.getMenus().forEach((menu) => {
        if (menu !== except) this.closeMenu(menu);
      });
    }

    openDrawer() {
      if (!this.drawer) return;
      document.dispatchEvent(new CustomEvent('so-premium:drawer-open', { detail: this.root }));
      this.lastFocus = document.activeElement;
      this.closeMenus();
      this.closeSearch(false);
      this.root.classList.add('is-drawer-open');
      this.drawer.setAttribute('aria-hidden', 'false');
      this.drawerOpen?.setAttribute('aria-expanded', 'true');
      document.body.classList.add('so-premium-scroll-locked');
      this.drawerClose?.focus();
    }

    closeDrawer(restoreFocus = true) {
      if (!this.drawer || !this.root.classList.contains('is-drawer-open')) return;
      this.root.classList.remove('is-drawer-open');
      this.drawer.setAttribute('aria-hidden', 'true');
      this.drawerOpen?.setAttribute('aria-expanded', 'false');
      if (!document.querySelector('.so-premium-root.is-drawer-open')) {
        document.body.classList.remove('so-premium-scroll-locked');
      }
      if (restoreFocus && this.lastFocus instanceof HTMLElement) this.lastFocus.focus();
    }

    openSearch() {
      if (!this.search) return;
      this.closeMenus();
      this.search.classList.add('is-open');
      this.search.setAttribute('aria-hidden', 'false');
      this.searchOpen?.setAttribute('aria-expanded', 'true');
      window.requestAnimationFrame(() => this.searchInput?.focus());
    }

    closeSearch(restoreFocus = false) {
      if (!this.search) return;
      this.search.classList.remove('is-open');
      this.search.setAttribute('aria-hidden', 'true');
      this.searchOpen?.setAttribute('aria-expanded', 'false');
      if (restoreFocus) this.searchOpen?.focus();
    }

    toggleSearch() {
      if (this.search?.classList.contains('is-open')) {
        this.closeSearch(true);
      } else {
        this.openSearch();
      }
    }

    trapDrawerFocus(event) {
      if (event.key !== 'Tab' || !this.drawer) return;
      const focusable = [...this.drawer.querySelectorAll(focusableSelector)]
        .filter((element) => !element.hasAttribute('hidden') && element.offsetParent !== null);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    handleKeydown(event) {
      if (this.root.classList.contains('is-drawer-open')) {
        if (event.key === 'Escape') {
          event.preventDefault();
          this.closeDrawer();
          return;
        }
        this.trapDrawerFocus(event);
        return;
      }

      if (event.key === 'Escape') {
        const openTrigger = this.root.querySelector('[data-so-premium-menu].is-open [data-so-premium-menu-trigger]');
        const searchWasOpen = this.search?.classList.contains('is-open');
        this.closeMenus();
        this.closeSearch(Boolean(searchWasOpen));
        if (!searchWasOpen) openTrigger?.focus();
      }

      if (event.key === 'ArrowDown' && document.activeElement?.matches('[data-so-premium-menu-trigger]')) {
        event.preventDefault();
        const menu = document.activeElement.closest('[data-so-premium-menu]');
        this.openMenu(menu);
        menu.querySelector('[data-so-premium-menu-panel] a')?.focus();
      }
    }

    destroy() {
      this.abortController.abort();
      this.closeDrawer(false);
      this.sectionHost?.classList.remove('so-premium-section--sticky');
      this.root.dataset.soPremiumReady = 'false';
    }
  }

  const initialize = (scope = document) => {
    scope.querySelectorAll('[data-so-premium-root]').forEach((root) => new SOHeaderPremium(root));
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize(), { once: true });
  } else {
    initialize();
  }

  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
})();
